import { Check, Clipboard, ExternalLink, QrCode, Share2, X } from "lucide-react"
import QRCode from "qrcode"
import { useEffect, useState } from "react"
import type { PairingSession } from "../../computers/context"
import { getPlatformAdapter } from "../../platform/adapter"
import { copyTextToClipboard } from "../../utils/clipboard"
import { RevealOnMount } from "./RevealOnMount"
import { remainingLabel } from "./sharingCopy"

/**
 * A one-time invitation for one of your own devices. The QR code is the
 * hero because the common case is a phone held up to the screen; the link
 * actions sit beside it for everything else.
 */
export function AddDevicePanel({ pairing, computerName, onClose }: { pairing: PairingSession; computerName: string; onClose(): void }) {
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [actionError, setActionError] = useState<string | null>(null)
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        let active = true
        void QRCode.toDataURL(pairing.inviteUrl, { errorCorrectionLevel: "M", margin: 1, width: 320 })
            .then((value) => {
                if (active) setQrCode(value)
            })
            .catch((error) => {
                if (active) setActionError(error instanceof Error ? error.message : String(error))
            })
        return () => {
            active = false
        }
    }, [pairing.inviteUrl])

    useEffect(() => {
        const interval = globalThis.setInterval(() => setNow(Date.now()), 1_000)
        return () => globalThis.clearInterval(interval)
    }, [])

    const copyLink = async () => {
        try {
            setActionError(null)
            await copyTextToClipboard(pairing.inviteUrl)
            setCopied(true)
            globalThis.setTimeout(() => setCopied(false), 1_500)
        } catch (error) {
            setActionError(error instanceof Error ? error.message : String(error))
        }
    }

    const shareLink = async () => {
        try {
            setActionError(null)
            const result = await getPlatformAdapter().share({
                title: "Pair with WildLark",
                text: "Open this one-time link on your device.",
                url: pairing.inviteUrl,
            })
            if (result.status === "unavailable") {
                await copyLink()
                return
            }
            if (result.status !== "ok") throw new Error(result.status === "failed" ? result.message : result.reason)
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") return
            setActionError(error instanceof Error ? error.message : String(error))
        }
    }

    const openLink = async () => {
        try {
            setActionError(null)
            const result = await getPlatformAdapter().openExternalUrl(pairing.inviteUrl)
            if (result.status !== "ok") throw new Error(result.status === "failed" ? result.message : result.reason)
        } catch (error) {
            setActionError(error instanceof Error ? error.message : String(error))
        }
    }

    const remaining = remainingLabel(pairing.expiresAt, now)

    return (
        <RevealOnMount className="sharing-rise border border-primary/40 bg-primary/5 p-4" data-add-device-panel="true">
            <div className="grid gap-5 sm:grid-cols-[168px_1fr]">
                <div className="flex h-[168px] w-[168px] items-center justify-center bg-white p-2 max-[600px]:mx-auto">
                    {qrCode ? (
                        <img src={qrCode} alt="One-time WildLark pairing QR code" className="h-full w-full" />
                    ) : (
                        <QrCode className="h-9 w-9 text-black/30" />
                    )}
                </div>
                <div className="min-w-0">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-base-content">Add a device to {computerName}</div>
                            <p className="mt-1 text-xs leading-5 text-muted">
                                Point your phone's camera at the code, or send yourself the link. It works once and carries everything the device needs.
                            </p>
                        </div>
                        <button
                            type="button"
                            className="btn flex h-8 w-8 flex-none items-center justify-center text-muted hover:bg-base-200 hover:text-base-content"
                            onClick={onClose}
                            aria-label="Close invitation"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn flex h-9 items-center gap-1.5 border border-border bg-base-200 px-3 text-xs font-medium text-base-content hover:bg-base-300/60 max-[600px]:h-11 max-[600px]:flex-1 max-[600px]:justify-center"
                            onClick={() => void copyLink()}
                        >
                            {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Clipboard className="h-3.5 w-3.5" />}
                            {copied ? "Copied" : "Copy link"}
                        </button>
                        <button
                            type="button"
                            className="btn flex h-9 items-center gap-1.5 border border-border bg-base-200 px-3 text-xs font-medium text-base-content hover:bg-base-300/60 max-[600px]:h-11 max-[600px]:flex-1 max-[600px]:justify-center"
                            onClick={() => void shareLink()}
                        >
                            <Share2 className="h-3.5 w-3.5" />
                            Share
                        </button>
                        <button
                            type="button"
                            className="btn flex h-9 items-center gap-1.5 border border-border bg-base-200 px-3 text-xs font-medium text-base-content hover:bg-base-300/60 max-[600px]:h-11 max-[600px]:basis-full max-[600px]:justify-center"
                            onClick={() => void openLink()}
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            Open in browser
                        </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-[11px] text-muted">
                        <span className="sharing-pulse inline-block h-1.5 w-1.5 bg-primary" aria-hidden="true" />
                        {remaining === "Expired" ? "Expired. Make a new invitation." : `Waiting for the device · ${remaining}`}
                        {pairing.pushEnrollment && <span>· includes notifications</span>}
                    </div>
                    {actionError && <div className="mt-3 text-xs text-error">{actionError}</div>}
                </div>
            </div>
        </RevealOnMount>
    )
}
